/* eslint-disable react-hooks/exhaustive-deps */
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonToolbar,
  FormControl,
  InputGroup,
  OverlayTrigger,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from 'react-bootstrap';
// @ts-ignore @TODO: add types for package
import Toggle from 'react-bootstrap-toggle';
// @ts-ignore @TODO: add types for package
import { Confirm } from 'react-confirm-bootstrap';
import moment from 'moment';
import _ from 'lodash';
import Modal from 'react-modal';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import { fmtMoney } from '@upstart/harmony/lib/formatter';
import {
  CLIENT_TYPE,
  DealerCashRuleType,
  PriceRuleOffsetType,
  PriceRuleSource,
} from '@upstart/harmony/lib/constants';
import { Condition } from '@upstart/harmony/lib/legos/constants';
import {
  PriceRulesEntry,
  CarPriceRule,
  PriceRule,
  AprConfig,
  PriceRulesRevision,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { PriceRules } from '@upstart/harmony/lib/legos/priceRulesTypes';
import {
  AllTrimVinFilter,
  DealerForPriceMatrix,
  ModelEntry,
  PriceRulesEntryUpdater,
  TrimEntry,
  TrimEntrySampleCar,
  UserForPriceMatrix,
} from './types';
import DecodeService from '../../services/DecodeService';
import {
  matchModelEntry,
  matchModelEntryWithoutVin,
} from './PricingMatrixEditor';
import ErrorMessage from '../ErrorMessage';
import ExpirationInput from './ExpirationInput';
import DealerCashModal from './DealerCashModal';
import useAprConfigModal from './useAprConfigModal';
import CarService from '../../services/CarService';
import {
  renderHelpToolTip,
  renderOffsetInput,
  stopPropagation,
} from './componentsUtils';
import { HEADER_TOOLTIPS, DEFAULT_DEALER_CASH_RULE_TYPE } from './constants';
import { limitMonetaryInput } from './utils';

const MODAL_STYLE = {
  content: {
    margin: '0 auto',
    maxWidth: '500px',
    position: 'relative',
  },
};

function showCarPriceValue(value: number | null | undefined) {
  return value !== undefined && value !== null ? fmtMoney(value) : null;
}

/**
 * Simulates what the pricing algorithm on the backend does for a price rule and a car
 *
 * @param sampleCar the sample car to apply the rule to
 * @param trimPriceRule the price rule to apply
 */
function calculatePriceForCar(
  sampleCar: TrimEntrySampleCar | undefined,
  trimPriceRule: PriceRule & Partial<CarPriceRule>,
) {
  // resolve the price source
  let value;
  switch (trimPriceRule.priceSource) {
    case PriceRuleSource.MSRP:
      value = sampleCar?.msrp;
      break;
    case PriceRuleSource.INVOICE:
      value = sampleCar?.invoice;
      break;
    case PriceRuleSource.FEED_PRICE:
    default:
      value = sampleCar?.price;
      break;
  }
  // resolve any offsets
  if (value && trimPriceRule.offsetValue) {
    switch (trimPriceRule.offsetType) {
      case PriceRuleOffsetType.SUBTRACT:
        value -= trimPriceRule.offsetValue;
        break;
      case PriceRuleOffsetType.ADD_PERCENT:
        value *= 1 + trimPriceRule.offsetValue / 100;
        break;
      case PriceRuleOffsetType.SUBTRACT_PERCENT:
        value *= 1 - trimPriceRule.offsetValue / 100;
        break;
      case PriceRuleOffsetType.ADD:
      default:
        value += trimPriceRule.offsetValue;
        break;
    }
  }
  // resolve any cash discounts
  if (value && trimPriceRule.discount) {
    value -= trimPriceRule.discount;
  }
  return value;
}

interface RuleUpdateInfo {
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Resolves the update info for a pricing rule
 * Has the following cases:
 *
 * Getting update info for a trim rule
 *  - Trim is in price rules
 *   > find latest entry in price history (case #1)
 *  - Trim is not in price rules
 *   - Model fallback is in price rules
 *    > find latest model fallback change (case #2) or trim price rule delete (case #3)
 *   - Model fallback is not in price rules
 *    > find latest trim price rule delete (case #4) or model fallback delete (case #5) or default rule fallback change (case #6)
 *
 * Getting update info for a model rule
 *  - Model is in price rules
 *   > find latest entry in price history (case #7)
 *  - Model in not in price rules
 *   > find latest model price rule delete (case #8) or default rule fallback change (case #9)
 *
 * Getting update info for a default rule
 *  > find latest default rule change (case #10)
 */
function getRuleHistoryEventUpdateInfo(
  clientType: CLIENT_TYPE,
  condition: Condition,
  users: UserForPriceMatrix[],
  priceRules?: PriceRulesEntry,
  priceRulesRevisions?: PriceRulesRevision[],
  modelEntry?: ModelEntry,
  trim?: string,
  vin?: string,
) {
  let ruleRevision: PriceRulesRevision | undefined;
  const existingRule =
    modelEntry &&
    priceRules?.carRules?.find(
      matchModelEntry(modelEntry, true, trim, true, vin),
    );
  if (existingRule) {
    // case #1 and #7: the model/trim rule is currently enabled so find the latest history entry where it was changed
    ruleRevision = priceRulesRevisions?.find(revision =>
      revision?.diff?.[clientType]?.[condition]?.carRulesDiff?.find(
        diff => diff.id === existingRule.id,
      ),
    );
  } else {
    let trimHasExistingModelRule = false;
    if (modelEntry && trim !== undefined) {
      const existingModelRule =
        modelEntry &&
        priceRules?.carRules?.find(matchModelEntry(modelEntry, true));
      if (existingModelRule) {
        trimHasExistingModelRule = true;
        // case #2 and #3: the model rule that this trim rule can fallback to is enabled
        // so find the latest history entry where it was changed or the trim rule was deleted
        ruleRevision = priceRulesRevisions?.find(revision =>
          revision?.diff?.[clientType]?.[condition]?.carRulesDiff?.find(
            diff =>
              // case #2
              diff.id === existingModelRule.id ||
              // case #3
              (diff.type === 'remove' &&
                matchModelEntry(modelEntry, true, trim)(diff.rule)),
          ),
        );
      }
    }
    if (!trimHasExistingModelRule) {
      ruleRevision = priceRulesRevisions?.find(revision => {
        const eventRulesEntry = revision?.diff?.[clientType]?.[condition];
        if (modelEntry) {
          // case #4, 5, and #8: the model rule wasn't enabled or the trim rule, and its model rule fallback, weren't enabled
          // so find the latest history entry where the model rule or trim rule with its possible fallback were removed
          const result = eventRulesEntry?.carRulesDiff?.find(
            diff =>
              diff.type === 'remove' &&
              (matchModelEntry(modelEntry, true)(diff.rule) ||
                matchModelEntry(modelEntry, true, trim)(diff.rule)),
          );
          if (result) return true;
        }
        // case #6, #9, and #10: find the latest history entry with a default rule change, which every rule falls back to
        return eventRulesEntry?.defaultRuleDiff !== undefined;
      });
    }
  }
  // get the final user and display their name and the time of the edit
  const updatingUser =
    ruleRevision && users.find(u => u.id === ruleRevision?.userId);

  let updatedBy;
  if (updatingUser) {
    if (updatingUser.firstName || updatingUser.lastName) {
      updatedBy = `${updatingUser.firstName || ''} ${updatingUser.lastName ||
        ''}`;
    } else if (updatingUser.email || updatingUser.username) {
      updatedBy = updatingUser.email || updatingUser.username;
    }
  }

  return {
    updatedAt: ruleRevision?.createdAt,
    updatedBy,
  };
}

const renderPriceRuleSourceSelector = (
  condition: Condition,
  value: PriceRuleSource,
  setValue: (value: PriceRuleSource) => void,
) => (
  <ButtonToolbar>
    <ToggleButtonGroup
      type="radio"
      name="priceType"
      value={value}
      onChange={setValue}
    >
      {/* Used and Cpo vehicles do not have MSRP */}
      {condition === Condition.NEW && (
        <ToggleButton value={PriceRuleSource.MSRP}>MSRP</ToggleButton>
      )}
      <ToggleButton value={PriceRuleSource.INVOICE}>Invoice</ToggleButton>
      <ToggleButton value={PriceRuleSource.FEED_PRICE}>Feed Price</ToggleButton>
    </ToggleButtonGroup>
  </ButtonToolbar>
);

type DealerCashRuleValueByFinance = {
  lease?: number;
  cash?: number;
  purchase?: number;
};

const renderDealerCashRuleInput = ({
  type,
  value,
  setValue,
  setType,
  financeTypeValues,
}: {
  type: DealerCashRuleType | undefined;
  value: number | undefined | null;
  setValue: (offset: number | undefined) => void;
  setType?: (type: DealerCashRuleType) => void;
  financeTypeValues?: DealerCashRuleValueByFinance | undefined;
}) => {
  let hasMultipleValues = false;
  // If there are values in financeTypeValues for dealer cash, show multiple in input instead of value
  if (financeTypeValues && Object.keys(financeTypeValues).length > 0) {
    hasMultipleValues = true;
  }

  return (
    <InputGroup className="dealer-cash-rule">
      <InputGroup.Addon className={setType ? '' : 'dealer-cash-add-on'}>
        {setType ? (
          <FormControl
            componentClass="select"
            placeholder="select"
            value={type || DEFAULT_DEALER_CASH_RULE_TYPE}
            onChange={event =>
              setType(
                (event.target as HTMLInputElement).value as DealerCashRuleType,
              )
            }
          >
            <option value={DealerCashRuleType.PRICE_OFFSET_FIXED_MAX}>-</option>
            <option value={DealerCashRuleType.PRICE_OFFSET_PERCENT}>- %</option>
          </FormControl>
        ) : (
          <>
            <Icon
              name="long arrow alternate up"
              size="large"
              className="toggle-icon dealer-cash-icon"
              onClick={() => {
                if (value) {
                  setValue(value + 1);
                } else {
                  setValue(1);
                }
              }}
            />
            <Icon
              name="long arrow alternate down"
              size="large"
              className="toggle-icon dealer-cash-icon"
              onClick={() => {
                if (value && value > 1) {
                  setValue(value - 1);
                } else {
                  setValue(0);
                }
              }}
            />
          </>
        )}
      </InputGroup.Addon>
      {!setType &&
        (type === DealerCashRuleType.PRICE_OFFSET_PERCENT ? (
          <InputGroup.Addon className="dealer-cash-percent-label">
            <div>%</div>
          </InputGroup.Addon>
        ) : (
          <InputGroup.Addon className="dealer-cash-dollar-label">
            <div>$</div>
          </InputGroup.Addon>
        ))}
      {hasMultipleValues ? (
        <FormControl
          type="input"
          disabled
          value={'Multiple'}
          className="dealer-cash-multiple-label"
        />
      ) : (
        <FormControl
          type="number"
          value={value ?? ''}
          onChange={event => {
            const newValue = (event.target as HTMLInputElement).value.replace(
              /-/g,
              '',
            );
            const number = newValue === '' ? undefined : parseInt(newValue, 10);
            setValue(number);
          }}
        />
      )}
    </InputGroup>
  );
};

const renderDiscountInput = (
  discount: number | undefined,
  setDiscount: (discount: number | undefined) => void,
) => (
  <FormControl
    type="number"
    placeholder="0"
    value={discount}
    onChange={event =>
      setDiscount(parseInt((event.target as HTMLInputElement).value, 10))
    }
  />
);

function TrimOrVinRule({
  clientType,
  rule,
  dealer,
  condition,
  modelEntry,
  index,
  setExpandedTrims,
  expandedTrims,
  sampleCar,
  updateTrimPriceRule,
  dealerCashEnabled,
  generateRuleId,
  hasMatchingVinRules,
  allTrimVinFilter,
  setShowVinRemove,
  setRuleToRemove,
}: {
  clientType: CLIENT_TYPE;
  rule: PriceRule & Partial<CarPriceRule> & RuleUpdateInfo;
  dealer: DealerForPriceMatrix | undefined;
  condition: Condition;
  modelEntry: ModelEntry;
  index: number;
  setExpandedTrims: (obj: Record<string, boolean>) => void;
  expandedTrims: Record<string, boolean>;
  sampleCar: TrimEntry['sampleCar'];
  updateTrimPriceRule: (
    trimPriceRule: PriceRule & Partial<CarPriceRule>,
    update: Partial<CarPriceRule> & { enabled?: boolean },
  ) => void;
  dealerCashEnabled: boolean;
  generateRuleId: (
    modelEntry: ModelEntry,
    trim?: string,
    vin?: string,
  ) => string;
  hasMatchingVinRules: boolean;
  allTrimVinFilter: AllTrimVinFilter;
  setShowVinRemove: (val: boolean) => void;
  setRuleToRemove: (
    val: PriceRule & Partial<CarPriceRule> & RuleUpdateInfo,
  ) => void;
}) {
  const [dealerCashModalOpen, setDealerCashModalOpen] = useState(false);

  const closeDealerCash = () => setDealerCashModalOpen(false);

  const openDealerCashModal = () => setDealerCashModalOpen(true);

  const isVinRule = !!rule.vin;

  const { aprConfigModal, aprConfigButton } = useAprConfigModal({
    dealer,
    clientType,
    condition,
    modelEntry,
    rule,
    aprConfig: rule?.aprConfig,
    onSave: (newAprConfig: AprConfig | undefined) => {
      updateTrimPriceRule(rule, { aprConfig: newAprConfig });
    },
  });

  return (
    <>
      <tr
        className={`trim-rule ${rule.id ? '' : 'disabled'}`}
        key={isVinRule ? rule.id : index}
      >
        <td>
          <div className="trim-name">
            <h5>
              {isVinRule ? (
                <div>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span
                    style={{ cursor: 'pointer', fontSize: '20px' }}
                    onClick={() => {
                      setRuleToRemove(rule);
                      setShowVinRemove(true);
                    }}
                  >
                    {'×'}
                  </span>{' '}
                  VIN {rule.vin}
                </div>
              ) : (
                rule.trim
              )}
            </h5>
            {!isVinRule && rule.mfrModelCodes && (
              <OverlayTrigger
                placement="bottom"
                delayHide={300}
                overlay={
                  <Tooltip
                    id={`pricing-matrix-mfrcode-tooltip-${generateRuleId(
                      modelEntry,
                      rule.trim,
                    )}`}
                  >
                    Manufacturer Model Code
                  </Tooltip>
                }
              >
                <span className="trim-model-code">
                  ({rule.mfrModelCodes.map(mfrCode => mfrCode.code).join(', ')})
                </span>
              </OverlayTrigger>
            )}
            {hasMatchingVinRules && allTrimVinFilter !== AllTrimVinFilter.TRIM && (
              <span
                onClick={() =>
                  setExpandedTrims({
                    ...expandedTrims,
                    [index]: !expandedTrims[index],
                  })
                }
              >
                {expandedTrims[index] ? '▼' : '►'}
              </span>
            )}
          </div>
        </td>
        <td align="center">
          <div className="cell-content-center">
            {renderPriceRuleSourceSelector(
              condition,
              rule.priceSource,
              priceSource => updateTrimPriceRule(rule, { priceSource }),
            )}
          </div>
        </td>
        <td>
          <div className="cell-content-center">
            {renderOffsetInput(
              rule.offsetType,
              rule.offsetValue,
              offsetType => updateTrimPriceRule(rule, { offsetType }),
              offsetValue =>
                updateTrimPriceRule(rule, {
                  offsetValue: offsetValue
                    ? limitMonetaryInput(offsetValue)
                    : offsetValue,
                }),
            )}
          </div>
        </td>
        <td>
          <div className="cell-content-center">
            {renderDiscountInput(rule.discount, discount =>
              updateTrimPriceRule(rule, {
                discount: discount ? limitMonetaryInput(discount) : discount,
              }),
            )}
          </div>
        </td>
        {dealerCashEnabled && (
          <td>
            <div className="cell-content-center">
              <div className="dealer-cash-center">
                {renderDealerCashRuleInput({
                  type: rule.dealerCashRuleType,
                  value: rule.dealerCashRuleValue,
                  setType: dealerCashRuleType =>
                    updateTrimPriceRule(rule, {
                      dealerCashRuleType,
                    }),
                  setValue: dealerCashRuleValue =>
                    updateTrimPriceRule(rule, {
                      dealerCashRuleType:
                        rule.dealerCashRuleType ||
                        DEFAULT_DEALER_CASH_RULE_TYPE,
                      dealerCashRuleValue: dealerCashRuleValue
                        ? limitMonetaryInput(dealerCashRuleValue)
                        : dealerCashRuleValue,
                    }),
                  financeTypeValues: rule?.dealerCashRuleValueByFinance,
                })}
                <Icon
                  name="ellipsis vertical"
                  size="large"
                  className="toggle-icon dealer-cash-icon"
                  onClick={openDealerCashModal}
                />
              </div>
            </div>
          </td>
        )}
        <td>
          <div className="cell-content-center">
            <div onClick={stopPropagation}>
              <ExpirationInput
                dealer={dealer}
                value={rule.expiration}
                onChange={expiration =>
                  updateTrimPriceRule(rule, { expiration })
                }
              />
            </div>
          </div>
        </td>
        <td>
          <div className="cell-content-center">
            <Toggle
              on="On"
              off="Off"
              size="sm"
              className="toggle-switch"
              active={!!rule.id}
              onClick={(enabled: boolean) =>
                updateTrimPriceRule(rule, { enabled })
              }
              disabled={isVinRule}
            />
          </div>
        </td>
        <td className="price-cell">
          {showCarPriceValue(calculatePriceForCar(sampleCar, rule))}
          {sampleCar &&
            renderHelpToolTip(
              `pricing-matrix-deal-price-help-${generateRuleId(
                modelEntry,
                rule.trim,
              )}`,
              `Values from ${
                sampleCar.stock ? `Stock ${sampleCar.stock}` : sampleCar.id
              }`,
            )}
        </td>
        <td className="price-cell">{showCarPriceValue(sampleCar?.msrp)}</td>
        <td className="price-cell">{showCarPriceValue(sampleCar?.invoice)}</td>
        <td className="price-cell">{showCarPriceValue(sampleCar?.price)}</td>
        <td>
          {rule.updatedAt ? moment(rule.updatedAt).format('YYYY-MM-DD') : ''}{' '}
          <br />
          {rule.updatedBy ?? null}
        </td>
        <td className="cell-content-right">{aprConfigButton}</td>
      </tr>
      <DealerCashModal
        close={closeDealerCash}
        type={rule.dealerCashRuleType}
        dealerCashValue={rule}
        renderDealerCashRuleInput={renderDealerCashRuleInput}
        resetToDefault={() =>
          updateTrimPriceRule(rule, {
            dealerCashRuleValueByFinance: {},
          })
        }
        setLeaseValue={(value: number | undefined) =>
          updateTrimPriceRule(rule, {
            dealerCashRuleValueByFinance: {
              ...rule.dealerCashRuleValueByFinance,
              lease: value ? limitMonetaryInput(value) : value,
            },
          })
        }
        setCashValue={(value: number | undefined) =>
          updateTrimPriceRule(rule, {
            dealerCashRuleValueByFinance: {
              ...rule.dealerCashRuleValueByFinance,
              cash: value ? limitMonetaryInput(value) : value,
            },
          })
        }
        setPurchaseValue={(value: number | undefined) =>
          updateTrimPriceRule(rule, {
            dealerCashRuleValueByFinance: {
              ...rule.dealerCashRuleValueByFinance,
              purchase: value ? limitMonetaryInput(value) : value,
            },
          })
        }
        open={dealerCashModalOpen}
      />
      {aprConfigModal}
    </>
  );
}

interface DefaultRulesTableProps {
  dealer?: DealerForPriceMatrix;
  users: UserForPriceMatrix[];
  clientType: CLIENT_TYPE;
  condition: Condition;
  priceRules: PriceRulesEntry;
  updatePriceRules: PriceRulesEntryUpdater;
  priceRulesRevisions?: PriceRulesRevision[];
  dealerCashEnabled: boolean;
  onDealerCashEnabledChange: (enabled: boolean) => void;
  selectInventoryRebateEnabled: boolean;
  onSelectInventoryRebateEnabledChange: (enabled: boolean) => void;
  dealerPriceRules?: PriceRules;
}

export const DefaultRulesTable: FunctionComponent<DefaultRulesTableProps> = ({
  dealer,
  users,
  clientType,
  condition,
  priceRules,
  updatePriceRules,
  priceRulesRevisions,
  dealerCashEnabled,
  onDealerCashEnabledChange,
  selectInventoryRebateEnabled,
  onSelectInventoryRebateEnabledChange,
  dealerPriceRules,
}) => {
  const [dealerCashModalOpen, setDealerCashModalOpen] = useState(false);

  const closeDealerCash = () => setDealerCashModalOpen(false);

  const openDealerCashModal = () => setDealerCashModalOpen(true);

  const { aprConfigModal, aprConfigButton } = useAprConfigModal({
    dealer,
    clientType,
    condition,
    aprConfig: priceRules?.defaultRule?.aprConfig,
    onSave: (newAprConfig: AprConfig | undefined) => {
      updatePriceRules(newPriceRules => {
        newPriceRules.defaultRule.aprConfig = newAprConfig;
      });
    },
  });

  const { updatedAt, updatedBy } = getRuleHistoryEventUpdateInfo(
    clientType,
    condition,
    users,
    dealerPriceRules?.data?.[clientType]?.[condition],
    priceRulesRevisions,
  );
  return (
    <div className="default-rule-table-container">
      <table className="rule-table default-rule-table">
        <thead>
          <tr>
            <th> Type </th>
            <th>
              <div className="cell-content-center-header">
                Source Price{' '}
                {renderHelpToolTip(
                  'pricing-matrix-source-header-help-default-rule',
                  HEADER_TOOLTIPS.SOURCE,
                )}
              </div>
            </th>
            <th>
              <div className="cell-content-center-header">
                Offset{' '}
                {renderHelpToolTip(
                  'pricing-matrix-offset-header-help-default-rule',
                  HEADER_TOOLTIPS.OFFSET,
                )}
              </div>
            </th>
            {dealerCashEnabled && (
              <th>
                <div className="cell-content-center-header">
                  Dealer Cash{' '}
                  {renderHelpToolTip(
                    'pricing-matrix-dealer-cash-header-help-default-rule',
                    HEADER_TOOLTIPS.DEALER_CASH,
                  )}
                </div>
              </th>
            )}
            {/* CPO possibly has dealer cash and rebates */}
            {(condition === Condition.NEW || condition === Condition.CPO) && (
              <>
                <th>
                  <div className="cell-content-center-header">
                    Enable Dealer Cash
                  </div>
                </th>
                <th>
                  <div className="cell-content-center-header">
                    Enable Select Inventory Rebate
                  </div>
                </th>
              </>
            )}
            <th>
              <div className="cell-content-center-header">
                Updated{' '}
                {renderHelpToolTip(
                  'pricing-matrix-updated-header-help-default-rule',
                  HEADER_TOOLTIPS.UPDATED,
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <h4>Default for all {condition.toUpperCase()} vehicles</h4>
            </td>
            <td align="center">
              <div className="cell-content-center">
                {renderPriceRuleSourceSelector(
                  condition,
                  priceRules.defaultRule.priceSource,
                  value =>
                    updatePriceRules(newPriceRules => {
                      newPriceRules.defaultRule.priceSource = value;
                    }),
                )}
              </div>
            </td>
            <td>
              <div className="cell-content-center">
                {renderOffsetInput(
                  priceRules.defaultRule.offsetType,
                  priceRules.defaultRule.offsetValue,
                  type =>
                    updatePriceRules(newPriceRules => {
                      newPriceRules.defaultRule.offsetType = type;
                    }),
                  offset =>
                    updatePriceRules(newPriceRules => {
                      newPriceRules.defaultRule.offsetValue = offset
                        ? limitMonetaryInput(offset)
                        : offset;
                    }),
                )}
              </div>
            </td>
            {dealerCashEnabled && (
              <td>
                <div className="cell-content-center">
                  {renderDealerCashRuleInput({
                    type: priceRules.defaultRule.dealerCashRuleType,
                    value: priceRules.defaultRule.dealerCashRuleValue,
                    setType: type =>
                      updatePriceRules(newPriceRules => {
                        newPriceRules.defaultRule.dealerCashRuleType = type;
                      }),
                    setValue: value =>
                      updatePriceRules(newPriceRules => {
                        newPriceRules.defaultRule.dealerCashRuleType =
                          newPriceRules.defaultRule.dealerCashRuleType ||
                          DEFAULT_DEALER_CASH_RULE_TYPE;
                        newPriceRules.defaultRule.dealerCashRuleValue = value
                          ? limitMonetaryInput(value)
                          : value;
                      }),
                    financeTypeValues:
                      priceRules.defaultRule.dealerCashRuleValueByFinance,
                  })}
                  <Icon
                    name="ellipsis vertical"
                    size="large"
                    className="toggle-icon dealer-cash-icon"
                    onClick={openDealerCashModal}
                  />
                </div>
              </td>
            )}
            {/* CPO possibly has Dealer cash/rebates */}
            {(condition === Condition.NEW || condition === Condition.CPO) && (
              <>
                <td>
                  <div className="cell-content-center">
                    <Toggle
                      on="On"
                      off="Off"
                      size="sm"
                      className="toggle-switch"
                      active={dealerCashEnabled}
                      onClick={onDealerCashEnabledChange}
                    />
                  </div>
                </td>
                <td>
                  <div className="cell-content-center">
                    <Toggle
                      on="On"
                      off="Off"
                      size="sm"
                      className="toggle-switch"
                      active={selectInventoryRebateEnabled}
                      onClick={onSelectInventoryRebateEnabledChange}
                    />
                  </div>
                </td>
              </>
            )}
            <td>
              {updatedAt ? moment(updatedAt).format('YYYY-MM-DD') : ''} <br />
              {updatedBy ?? null}
            </td>
            <td className="text-center">{aprConfigButton}</td>
            {(condition === Condition.NEW || condition === Condition.CPO) && (
              <td>
                <div className="cell-content-right">
                  <Confirm
                    onConfirm={() =>
                      updatePriceRules(newPriceRules => {
                        newPriceRules.carRules = [];
                      })
                    }
                    body="Are you sure you want to reset overrides? This will disable overrides on all models and trims."
                    confirmText="Reset"
                    title="Reset Overrides"
                  >
                    <Button bsStyle="primary">Reset Overrides</Button>
                  </Confirm>
                </div>
              </td>
            )}
          </tr>
        </tbody>
      </table>
      <DealerCashModal
        close={closeDealerCash}
        type={priceRules.defaultRule.dealerCashRuleType}
        dealerCashValue={priceRules.defaultRule}
        renderDealerCashRuleInput={renderDealerCashRuleInput}
        resetToDefault={() =>
          updatePriceRules(newPriceRules => {
            newPriceRules.defaultRule.dealerCashRuleValueByFinance = {};
          })
        }
        setLeaseValue={(value: number | undefined) =>
          updatePriceRules(newPriceRules => {
            newPriceRules.defaultRule.dealerCashRuleValueByFinance = {
              ...newPriceRules.defaultRule.dealerCashRuleValueByFinance,
              lease: value ? limitMonetaryInput(value) : value,
            };
          })
        }
        setCashValue={(value: number | undefined) =>
          updatePriceRules(newPriceRules => {
            newPriceRules.defaultRule.dealerCashRuleValueByFinance = {
              ...newPriceRules.defaultRule.dealerCashRuleValueByFinance,
              cash: value ? limitMonetaryInput(value) : value,
            };
          })
        }
        setPurchaseValue={(value: number | undefined) =>
          updatePriceRules(newPriceRules => {
            newPriceRules.defaultRule.dealerCashRuleValueByFinance = {
              ...newPriceRules.defaultRule.dealerCashRuleValueByFinance,
              purchase: value ? limitMonetaryInput(value) : value,
            };
          })
        }
        open={dealerCashModalOpen}
      />
      {aprConfigModal}
    </div>
  );
};

interface ModelRulesTableProps {
  dealer?: DealerForPriceMatrix;
  users: UserForPriceMatrix[];
  modelEntry: ModelEntry;
  clientType: CLIENT_TYPE;
  condition: Condition;
  priceRules: PriceRulesEntry;
  updatePriceRules: PriceRulesEntryUpdater;
  priceRulesRevisions?: PriceRulesRevision[];
  dealerCashEnabled: boolean;
  generateRuleId: (
    modelEntry: ModelEntry,
    trim?: string,
    vin?: string,
  ) => string;
  updateVinPriceRule: (
    vinPriceRule: PriceRule & Partial<CarPriceRule>,
    update: Partial<CarPriceRule> & { enabled?: boolean },
  ) => void;
  allTrimVinFilter: AllTrimVinFilter;
  dealerPriceRules?: PriceRules;
}

export const ModelRulesTable: FunctionComponent<ModelRulesTableProps> = ({
  dealer,
  users,
  clientType,
  condition,
  modelEntry,
  priceRules,
  updatePriceRules,
  priceRulesRevisions,
  dealerCashEnabled,
  generateRuleId,
  allTrimVinFilter,
  dealerPriceRules,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [expanded, setExpanded] = useState(false);
  const [expandedTrims, setExpandedTrims] = useState<Record<string, boolean>>(
    {},
  );
  const [trims, setTrims] = useState<TrimEntry[] | undefined>();
  const [sampleCarsMap, setSampleCarsMap] = useState<
    Record<string, TrimEntrySampleCar | null>
  >({});
  const [unmappedRules, setUnmappedRules] = useState<CarPriceRule[]>([]);
  const [showVinRemove, setShowVinRemove] = useState(false);
  const [ruleToRemove, setRuleToRemove] = useState<
    PriceRule & Partial<CarPriceRule> & RuleUpdateInfo
  >();

  const [dealerCashModalOpen, setDealerCashModalOpen] = useState(false);

  const closeDealerCash = () => setDealerCashModalOpen(false);

  const openDealerCashModal = () => setDealerCashModalOpen(true);

  // get the trims, with sample cars, for this model when expanded
  useEffect(() => {
    if (expanded && dealer && trims === undefined)
      DecodeService.getTrimsForModel({
        dealerId: dealer.id,
        year: modelEntry.year,
        make: modelEntry.make,
        model: modelEntry.model,
      })
        .then(v =>
          setTrims(
            v.map((vehicle: TrimEntry) => ({
              ...vehicle,
              year: parseInt(vehicle.year.toString(), 10),
            })),
          ),
        )
        .catch(() => {
          setErrorMessage(
            'There was an error trying to get the list of trims for this model',
          );
        });
  }, [expanded, dealer, trims]);
  // expand if there are active trim rules for this model
  useEffect(() => {
    if (
      priceRules.carRules
        ?.filter(matchModelEntry(modelEntry))
        .find(r => r.trim !== undefined)
    ) {
      setExpanded(true);
    }
  }, [modelEntry, dealer?.id]);
  // Get all rules that were not mapped to any trim
  useEffect(() => {
    const allRulesWithVinSet = priceRules?.carRules
      ?.filter(r => r.vin)
      .filter(matchModelEntry(modelEntry));
    const rulesThatDontMatchAnyTrim =
      allRulesWithVinSet?.filter(
        r =>
          !trims?.some(
            trim =>
              trim.year === r.year &&
              _.lowerCase(trim.make) === _.lowerCase(r.make) &&
              _.lowerCase(trim.model) === _.lowerCase(r.model) &&
              _.lowerCase(trim.trim) === _.lowerCase(r.trim),
          ),
      ) || [];
    setUnmappedRules(rulesThatDontMatchAnyTrim);
  }, [users, modelEntry, priceRules, trims]);

  // Maintains and fetches samplecar details
  useEffect(() => {
    const allRulesWithVinSet = priceRules?.carRules
      ?.filter(r => r.vin)
      .filter(matchModelEntry(modelEntry));
    allRulesWithVinSet?.forEach(rule => {
      const vin = rule.vin;
      if (vin && sampleCarsMap[vin] === undefined) {
        CarService.getCar(vin)
          .then((car: TrimEntrySampleCar) => {
            if (car.id === vin) {
              setSampleCarsMap({ ...sampleCarsMap, [vin]: car });
              return;
            }
            setSampleCarsMap({ ...sampleCarsMap, [vin]: null });
          })
          .catch(() => {
            setSampleCarsMap({ ...sampleCarsMap, [vin]: null });
          });
      }
    });
  }, [priceRules]);

  // get the rule for this model
  const modelPriceRule: PriceRule &
    Partial<CarPriceRule> &
    RuleUpdateInfo = useMemo(() => {
    // get the model rule, falling back to the default rule
    const rule =
      priceRules.carRules?.find(matchModelEntry(modelEntry, true)) ||
      priceRules.defaultRule;
    // get the last update information
    const { updatedAt, updatedBy } = getRuleHistoryEventUpdateInfo(
      clientType,
      condition,
      users,
      dealerPriceRules?.data?.[clientType]?.[condition],
      priceRulesRevisions,
      modelEntry,
    );
    return { ...rule, updatedAt, updatedBy };
  }, [users, modelEntry, priceRules]);

  /**
   * Helper function for updating a model price rule
   *
   * @param update the properties to change in the price rule
   */
  const updateModelPriceRule = (
    update: Partial<CarPriceRule> & { enabled?: boolean },
  ) => {
    updatePriceRules(newPriceRules => {
      if (!modelPriceRule.id || !newPriceRules.carRules) {
        // if the rule doesn't exist, add it
        delete update.enabled;
        newPriceRules.carRules = newPriceRules.carRules || [];
        newPriceRules.carRules.push({
          ...modelPriceRule,
          ...update,
          id: generateRuleId(modelEntry),
          ...modelEntry,
        });
      } else {
        const ruleIndex = newPriceRules.carRules.findIndex(
          r => r.id === modelPriceRule.id,
        );
        if (update.enabled === false) {
          // if the rule is being disabled, delete it
          newPriceRules.carRules.splice(ruleIndex, 1);
        } else {
          // update the existing rule
          newPriceRules.carRules[ruleIndex] = {
            ...newPriceRules.carRules?.[ruleIndex],
            ...update,
          };
        }
      }
    });
  };

  const { aprConfigModal, aprConfigButton } = useAprConfigModal({
    dealer,
    clientType,
    condition,
    modelEntry,
    aprConfig: modelPriceRule?.aprConfig,
    onSave: (newAprConfig: AprConfig | undefined) => {
      updateModelPriceRule({ aprConfig: newAprConfig });
    },
  });

  // get the rules for this model's trims
  const trimPriceRules:
    | {
        rule: PriceRule & Partial<CarPriceRule> & RuleUpdateInfo;
        sampleCar: TrimEntry['sampleCar'];
      }[]
    | undefined = useMemo(
    () =>
      // maps each trim, for this model, to a rule
      trims?.map(trimEntry => {
        // get the trim rule, falling back to the model rule (which falls back to the default rule if necessary)
        const rule = priceRules.carRules?.find(
          matchModelEntryWithoutVin(modelEntry, true, trimEntry.trim),
        ) || { ...modelPriceRule, enabled: false, id: undefined };
        // get the last update information
        const { updatedAt, updatedBy } = getRuleHistoryEventUpdateInfo(
          clientType,
          condition,
          users,
          dealerPriceRules?.data?.[clientType]?.[condition],
          priceRulesRevisions,
          modelEntry,
          trimEntry.trim,
        );
        return {
          rule: {
            ...rule,
            year: modelEntry.year,
            make: modelEntry.make,
            model: modelEntry.model,
            trim: trimEntry.trim,
            mfrModelCodes: trimEntry.mfrModelCodes,
            updatedAt,
            updatedBy,
          },
          sampleCar: trimEntry.sampleCar,
        };
      }),
    [users, trims, priceRules, modelEntry, modelPriceRule],
  );

  /**
   * Helper function for updating a trim price rule
   *
   * @param update the properties to change in the price rule
   */
  const updateTrimPriceRule = (
    trimPriceRule: PriceRule & Partial<CarPriceRule>,
    update: Partial<CarPriceRule> & { enabled?: boolean },
  ) => {
    updatePriceRules(newPriceRules => {
      if (!trimPriceRule.id || !newPriceRules.carRules) {
        // if the rule doesn't exist, add it
        delete update.enabled;
        newPriceRules.carRules = newPriceRules.carRules || [];
        newPriceRules.carRules.push({
          ...trimPriceRule,
          ...update,
          id: generateRuleId(modelEntry, trimPriceRule.trim),
          ...modelEntry,
        });
      } else {
        const ruleIndex = newPriceRules.carRules.findIndex(
          r => r.id === trimPriceRule.id,
        );
        if (update.enabled === false) {
          // if the rule is being disabled, delete it
          newPriceRules.carRules.splice(ruleIndex, 1);
        } else {
          // update the existing rule
          newPriceRules.carRules[ruleIndex] = {
            ...newPriceRules.carRules[ruleIndex],
            ...update,
          };
        }
      }
    });
  };

  const renderUnmappedTrims = () => {
    if (!unmappedRules.length) return false;
    if (allTrimVinFilter === AllTrimVinFilter.TRIM) return false;
    const unmappedModelName = `${generateRuleId(modelEntry)}-unmapped`;
    // Set to expanded as default state
    if (expandedTrims[unmappedModelName] === undefined) {
      setExpandedTrims({
        ...expandedTrims,
        [unmappedModelName]: !expandedTrims[unmappedModelName],
      });
    }

    return (
      <>
        {allTrimVinFilter === AllTrimVinFilter.ALL && (
          <tr className={`trim-rule`}>
            <td colSpan={dealerCashEnabled ? 10 : 11}>
              <div className="trim-name">
                <h5>Unmapped Trims &nbsp;&nbsp;</h5>
                <span
                  onClick={() =>
                    setExpandedTrims({
                      ...expandedTrims,
                      [unmappedModelName]: !expandedTrims[unmappedModelName],
                    })
                  }
                >
                  {expandedTrims[unmappedModelName] ? '▼' : '►'}
                </span>
              </div>
            </td>
          </tr>
        )}
        {(expandedTrims[unmappedModelName] ||
          allTrimVinFilter === AllTrimVinFilter.VIN) &&
          unmappedRules?.map(vinPriceRule => {
            const sampleCar =
              sampleCarsMap[vinPriceRule.vin || ''] || undefined;
            return (
              <TrimOrVinRule
                clientType={clientType}
                rule={vinPriceRule}
                dealer={dealer}
                condition={condition}
                modelEntry={modelEntry}
                index={0}
                setExpandedTrims={setExpandedTrims}
                expandedTrims={expandedTrims}
                sampleCar={sampleCar}
                updateTrimPriceRule={updateTrimPriceRule}
                dealerCashEnabled={dealerCashEnabled}
                generateRuleId={generateRuleId}
                hasMatchingVinRules={false}
                allTrimVinFilter={allTrimVinFilter}
                setShowVinRemove={setShowVinRemove}
                setRuleToRemove={setRuleToRemove}
              />
            );
          })}
      </>
    );
  };

  const renderVinRemove = () => (
    <Modal
      isOpen
      // @ts-ignore
      style={MODAL_STYLE}
    >
      <h4 className="modal-title">REMOVE OVERRIDE</h4>
      <hr />
      Would you like to remove this vehicle: VIN {ruleToRemove?.vin} from this
      list?
      <hr />
      <div className="row modal-message">
        <div className="col-sm-3" />
        <div className="col-sm-3">
          <Button bsStyle="link" onClick={() => setShowVinRemove(false)}>
            Cancel
          </Button>
        </div>
        <div className="col-sm-3">
          <Button
            bsStyle="primary"
            onClick={() => {
              if (ruleToRemove)
                updateTrimPriceRule(ruleToRemove, { enabled: false });
              setShowVinRemove(false);
            }}
          >
            Remove
          </Button>
        </div>
        <div className="col-sm-3" />
      </div>
    </Modal>
  );

  return (
    <div className="model-rule-table-container">
      {showVinRemove && renderVinRemove()}
      <table className="rule-table model-rule-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>
              <div className="cell-content-center-header">
                Source Price{' '}
                {renderHelpToolTip(
                  `pricing-matrix-source-header-help-${generateRuleId(
                    modelEntry,
                  )}`,
                  HEADER_TOOLTIPS.SOURCE,
                )}
              </div>
            </th>
            <th>
              <div className="cell-content-center-header">
                Offset{' '}
                {renderHelpToolTip(
                  `pricing-matrix-offset-header-help-${generateRuleId(
                    modelEntry,
                  )}`,
                  HEADER_TOOLTIPS.OFFSET,
                )}
              </div>
            </th>
            <th>Cash Discount</th>
            {dealerCashEnabled && (
              <th>
                <div className="cell-content-center-header">
                  Dealer Cash{' '}
                  {renderHelpToolTip(
                    `pricing-matrix-dealer-cash-header-help-${generateRuleId(
                      modelEntry,
                    )}`,
                    HEADER_TOOLTIPS.DEALER_CASH,
                  )}
                </div>
              </th>
            )}
            <th>Expiration</th>
            <th>
              <div className="cell-content-center-header">
                Override{' '}
                {renderHelpToolTip(
                  `pricing-matrix-override-header-help-${generateRuleId(
                    modelEntry,
                  )}`,
                  HEADER_TOOLTIPS.OVERRIDE,
                )}
              </div>
            </th>
            <th>
              <div className="cell-content-center-header">
                <span>Deal Price</span>{' '}
                {renderHelpToolTip(
                  `pricing-matrix-dealprice-header-help-${generateRuleId(
                    modelEntry,
                  )}`,
                  HEADER_TOOLTIPS.PRICE,
                )}
              </div>
            </th>
            <th>MSRP</th>
            <th>Invoice</th>
            <th>Feed Price</th>
            <th>
              <div className="cell-content-center-header">
                Updated{' '}
                {renderHelpToolTip(
                  `pricing-matrix-updated-header-help-${generateRuleId(
                    modelEntry,
                  )}`,
                  HEADER_TOOLTIPS.UPDATED,
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr onClick={() => setExpanded(!expanded)}>
            <td>
              <div className="model-name">
                <h4>{`${modelEntry.year} ${modelEntry.make} ${modelEntry.model}`}</h4>
                {expanded ? '▼' : '►'}
              </div>
            </td>
            <td align="center">
              <div className="cell-content-center">
                {renderPriceRuleSourceSelector(
                  condition,
                  modelPriceRule.priceSource,
                  priceSource => updateModelPriceRule({ priceSource }),
                )}
              </div>
            </td>
            <td>
              <div className="cell-content-center">
                <div onClick={stopPropagation}>
                  {renderOffsetInput(
                    modelPriceRule.offsetType,
                    modelPriceRule.offsetValue,
                    offsetType => updateModelPriceRule({ offsetType }),
                    offsetValue =>
                      updateModelPriceRule({
                        offsetValue: offsetValue
                          ? limitMonetaryInput(offsetValue)
                          : offsetValue,
                      }),
                  )}
                </div>
              </div>
            </td>
            <td>
              <div className="cell-content-center">
                <div onClick={stopPropagation}>
                  {renderDiscountInput(modelPriceRule.discount, discount =>
                    updateModelPriceRule({
                      discount: discount
                        ? limitMonetaryInput(discount)
                        : discount,
                    }),
                  )}
                </div>
              </div>
            </td>
            {dealerCashEnabled && (
              <td>
                <div className="cell-content-center">
                  <div className="dealer-cash-center" onClick={stopPropagation}>
                    {renderDealerCashRuleInput({
                      type: modelPriceRule.dealerCashRuleType,
                      value: modelPriceRule.dealerCashRuleValue,
                      setType: dealerCashRuleType =>
                        updateModelPriceRule({ dealerCashRuleType }),
                      setValue: dealerCashRuleValue =>
                        updateModelPriceRule({
                          dealerCashRuleType:
                            modelPriceRule.dealerCashRuleType ||
                            DEFAULT_DEALER_CASH_RULE_TYPE,
                          dealerCashRuleValue: dealerCashRuleValue
                            ? limitMonetaryInput(dealerCashRuleValue)
                            : dealerCashRuleValue,
                        }),
                      financeTypeValues:
                        modelPriceRule?.dealerCashRuleValueByFinance,
                    })}
                    <Icon
                      name="ellipsis vertical"
                      size="large"
                      className="toggle-icon dealer-cash-icon"
                      onClick={openDealerCashModal}
                    />
                  </div>
                </div>
              </td>
            )}
            <td>
              <div className="cell-content-center">
                <div onClick={stopPropagation}>
                  <ExpirationInput
                    dealer={dealer}
                    value={modelPriceRule.expiration}
                    onChange={expiration =>
                      updateModelPriceRule({ expiration })
                    }
                  />
                </div>
              </div>
            </td>
            <td>
              <div className="cell-content-center">
                <div onClick={stopPropagation}>
                  <Toggle
                    on="On"
                    off="Off"
                    size="sm"
                    className="toggle-switch"
                    active={!!modelPriceRule.id}
                    onClick={(enabled: boolean) =>
                      updateModelPriceRule({ enabled })
                    }
                  />
                </div>
              </div>
            </td>
            <td className="price-cell" />
            <td className="price-cell" />
            <td className="price-cell" />
            <td className="price-cell" />
            <td>
              {modelPriceRule.updatedAt
                ? moment(modelPriceRule.updatedAt).format('YYYY-MM-DD')
                : ''}{' '}
              <br />
              {modelPriceRule.updatedBy ?? null}
            </td>
            <td className="cell-content-right">{aprConfigButton}</td>
          </tr>
          {expanded &&
            !errorMessage &&
            (trimPriceRules?.map(
              ({ rule: trimPriceRule, sampleCar }, index) => {
                const vinPriceRules =
                  priceRules?.carRules?.filter(
                    r =>
                      r.year === trimPriceRule.year &&
                      _.lowerCase(r.make) === _.lowerCase(trimPriceRule.make) &&
                      _.lowerCase(r.model) ===
                        _.lowerCase(trimPriceRule.model) &&
                      _.lowerCase(r.trim) === _.lowerCase(trimPriceRule.trim) &&
                      r.vin,
                  ) || [];
                const hasMatchingVinRules = !!vinPriceRules.length;
                // Set to expanded as default state
                if (expandedTrims[index] === undefined) {
                  setExpandedTrims({
                    ...expandedTrims,
                    [index]: !expandedTrims[index],
                  });
                }
                return (
                  <>
                    {allTrimVinFilter !== AllTrimVinFilter.VIN && (
                      <TrimOrVinRule
                        clientType={clientType}
                        rule={trimPriceRule}
                        dealer={dealer}
                        condition={condition}
                        modelEntry={modelEntry}
                        index={index}
                        setExpandedTrims={setExpandedTrims}
                        expandedTrims={expandedTrims}
                        sampleCar={sampleCar}
                        updateTrimPriceRule={updateTrimPriceRule}
                        dealerCashEnabled={dealerCashEnabled}
                        generateRuleId={generateRuleId}
                        hasMatchingVinRules={hasMatchingVinRules}
                        allTrimVinFilter={allTrimVinFilter}
                        setShowVinRemove={setShowVinRemove}
                        setRuleToRemove={setRuleToRemove}
                      />
                    )}
                    {expandedTrims[index] &&
                      allTrimVinFilter !== AllTrimVinFilter.TRIM &&
                      vinPriceRules?.map(vinPriceRule => {
                        const vinSampleCar =
                          sampleCarsMap[vinPriceRule.vin || ''] || undefined;
                        return (
                          <TrimOrVinRule
                            clientType={clientType}
                            rule={vinPriceRule}
                            dealer={dealer}
                            condition={condition}
                            modelEntry={modelEntry}
                            index={index}
                            setExpandedTrims={setExpandedTrims}
                            expandedTrims={expandedTrims}
                            sampleCar={vinSampleCar}
                            updateTrimPriceRule={updateTrimPriceRule}
                            dealerCashEnabled={dealerCashEnabled}
                            generateRuleId={generateRuleId}
                            hasMatchingVinRules={false}
                            allTrimVinFilter={allTrimVinFilter}
                            setShowVinRemove={setShowVinRemove}
                            setRuleToRemove={setRuleToRemove}
                          />
                        );
                      })}
                  </>
                );
              },
            ) ?? (
              <tr>
                <td colSpan={10} align="center">
                  <Dimmer active inverted>
                    <Loader inverted content="Loading" />
                  </Dimmer>
                </td>
              </tr>
            ))}
          {expanded && !errorMessage && renderUnmappedTrims()}
          {errorMessage && (
            <tr>
              <td colSpan={10}>
                <ErrorMessage error={errorMessage} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <DealerCashModal
        close={closeDealerCash}
        type={modelPriceRule.dealerCashRuleType}
        dealerCashValue={modelPriceRule}
        renderDealerCashRuleInput={renderDealerCashRuleInput}
        resetToDefault={() =>
          updateTrimPriceRule(modelPriceRule, {
            dealerCashRuleValueByFinance: {},
          })
        }
        setLeaseValue={(value: number | undefined) =>
          updateTrimPriceRule(modelPriceRule, {
            dealerCashRuleValueByFinance: {
              ...modelPriceRule.dealerCashRuleValueByFinance,
              lease: value ? limitMonetaryInput(value) : value,
            },
          })
        }
        setCashValue={(value: number | undefined) =>
          updateTrimPriceRule(modelPriceRule, {
            dealerCashRuleValueByFinance: {
              ...modelPriceRule.dealerCashRuleValueByFinance,
              cash: value ? limitMonetaryInput(value) : value,
            },
          })
        }
        setPurchaseValue={(value: number | undefined) =>
          updateTrimPriceRule(modelPriceRule, {
            dealerCashRuleValueByFinance: {
              ...modelPriceRule.dealerCashRuleValueByFinance,
              purchase: value ? limitMonetaryInput(value) : value,
            },
          })
        }
        open={dealerCashModalOpen}
      />
      {aprConfigModal}
    </div>
  );
};
